import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Box, Paper, Typography } from '@mui/material';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { GRID_COLS } from '../../../utils/gridCells';
import {
  gridCellsOf, panelKind, panelLabel, setGridLayout,
} from './appBuilderModel';
import { PanelIcon } from './panelVisuals';

/**
 * Drag and resize the panels of a grid app. Positions use the same 12-column units as the
 * live app, drawn with shorter rows so the whole screen fits in the editor.
 */
export default function AppGridArranger({ def, canEdit, onChange }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    const measure = () => { if (ref.current) setWidth(ref.current.offsetWidth || 720); };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' && ref.current ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(ref.current);
    return () => observer?.disconnect();
  }, []);

  const cells = useMemo(() => gridCellsOf(def), [def]);
  const panels = useMemo(() => {
    const byId = new Map((def.panels || []).map((p) => [String(p.id), p]));
    return cells.map((c) => byId.get(String(c.i))).filter(Boolean);
  }, [def.panels, cells]);
  const commit = (layout) => onChange(setGridLayout(def, layout));

  return (
    <Box
      ref={ref}
      data-testid="grid-arranger"
      sx={{
        border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'action.hover', p: 0.5, minHeight: 120,
      }}
    >
      <GridLayout
        layout={cells}
        width={width}
        gridConfig={{ cols: GRID_COLS, rowHeight: 12, margin: [6, 6] }}
        dragConfig={{ enabled: !!canEdit }}
        resizeConfig={{ enabled: !!canEdit }}
        onDragStop={commit}
        onResizeStop={commit}
      >
        {panels.map((panel) => (
          <Paper
            key={panel.id}
            variant="outlined"
            data-testid={`grid-cell-${panel.id}`}
            sx={{
              p: 1, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', cursor: canEdit ? 'move' : 'default',
            }}
          >
            <PanelIcon kind={panelKind(panel)} size={24} />
            <Typography variant="caption" fontWeight={700} noWrap>
              {panel.config?.title || panelLabel(panel)}
            </Typography>
          </Paper>
        ))}
      </GridLayout>
    </Box>
  );
}
